/**
 * Shared cache invalidation utilities
 *
 * Prevents the "stale Cloud Run read overwrites fresh cache" race condition.
 * When a mutation (e.g. billable item CRUD) invalidates ['projects'], React Query
 * prefix-matches ALL project queries including ['projects', 'detail', uuid].
 * If the refetch returns stale data from a cold Cloud Run instance, it overwrites
 * recent edits to project_understanding, fee_assumptions, etc.
 *
 * Solution: invalidate list queries only, never the detail query, unless the
 * mutation directly modifies the project record itself.
 */
import type { QueryClient, QueryFilters } from '@tanstack/react-query';

/**
 * Invalidate project LIST queries without touching the detail cache.
 *
 * Use this from any mutation that indirectly affects project data (financial
 * summaries, counts, etc.) but does NOT directly modify the project record.
 *
 * Examples: billable item CRUD, scope changes, invoice creation.
 *
 * Do NOT use this when the mutation directly modifies the project (useUpdateProject,
 * useUpdateProjectInline) — those should manage their own cache strategy.
 */
export function invalidateProjectListQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      // Match all ['projects', ...] EXCEPT ['projects', 'detail', ...]
      return key[0] === 'projects' && key[1] !== 'detail';
    },
  });
}

/**
 * Matches every invoice LIST query cache — `['invoices', statusFilter, search]`
 * — while excluding the per-invoice detail caches and the utility queries.
 *
 * Exclusions, and why:
 * - `['invoices', <uuid>]` / `['invoices', <uuid>, 'full']` — detail caches.
 *   Detected by the hyphen in a UUID second key; list queries key on a status
 *   slug ('all', 'draft', 'sent', ...) which never contains one.
 * - `['invoices', 'next-number']` — the next-invoice-number utility query.
 *   It DOES contain a hyphen, so it would already fall out above; it is named
 *   explicitly so the intent survives a future rename to a hyphen-less key.
 *
 * Pair this with `getQueriesData(invoiceListQueryFilter)` on the snapshot side
 * of any optimistic write that uses `setQueriesData(invoiceListQueryFilter, …)`
 * — a write with no matching snapshot is an un-rollback-able silent loss
 * (v0.14.4 R5; see useInvoices.ts `useUpdateInvoiceInline`).
 *
 * NOTE (2026-08-03): this literal is still inlined VERBATIM at four other call
 * sites — `AddItemModal.tsx` (two), `InvoiceDetailPage.tsx`, and
 * `useInvoices.ts`. They were left alone deliberately (owned by other work in
 * flight); collapse them onto this helper when those files are next touched.
 */
export const invoiceListQueryFilter: QueryFilters = {
  predicate: (query) => {
    const key = query.queryKey;
    if (key[0] !== 'invoices' || key.length < 2) return false;
    const secondKey = key[1];
    if (typeof secondKey === 'string' && secondKey.includes('-')) return false;
    if (secondKey === 'next-number') return false;
    return true;
  },
};
